import {MutableRefObject, RefObject, useCallback, useRef, useState} from 'react';

import {
    CardNetworkAsset,
    CardNetworkChangeEvent,
    CardNetworkDetails,
    EventListener,
    EventTypes,
    IAssetsManager,
    ICardPaymentMethodManager,
    PrimerHeadlessCheckout,
} from '@primer-io/checkout-web';

import {logWithSentry} from 'libs/sentry/sentry';
import {debounce} from 'libs/utils/debounce/debounce';

import {
    primerCardExpiryOptions,
    primerCardNumberOptions,
    primerCardSecurityCodeOptions,
    PrimerErrorType,
    PrimerInstrumentTypes,
} from '../constants';

const useCardNetworkImage = ({assetsRef}: {assetsRef: RefObject<IAssetsManager | null>}) => {
    const [cardNetwork, setCardNetwork] = useState<CardNetworkAsset | null>(null);

    const createNetworkImage = async (networkDetails: CardNetworkDetails) => {
        if (assetsRef.current) {
            const asset = await assetsRef.current.getCardNetworkAsset(networkDetails.network);

            if (cardNetwork?.displayName === asset.displayName) {
                return;
            }
            setCardNetwork(asset);
        }
    };

    const onCardNetworksChangeDebounced = debounce(
        async ({detectedCardNetworks}: CardNetworkChangeEvent) => {
            // display the most likely network in a single-badge card
            const mostLikelyNetwork =
                detectedCardNetworks.preferred ?? detectedCardNetworks.items[0];
            if (!mostLikelyNetwork) {
                return;
            }
            await createNetworkImage(mostLikelyNetwork);
        },
        400,
    );

    return {cardNetwork, onCardNetworksChange: onCardNetworksChangeDebounced};
};

/**
 * hook for Primer payment configuration
 */
export const useConfigureCard = ({
    managerRef,
    primerRef,
    setPrimerError,
}: {
    primerRef: RefObject<PrimerHeadlessCheckout | null>;
    managerRef: MutableRefObject<ICardPaymentMethodManager | null>;
    setPrimerError: (
        error: PrimerErrorType | ((error: PrimerErrorType) => PrimerErrorType),
    ) => void;
}) => {
    const assetsRef = useRef<IAssetsManager | null>(null);
    const {cardNetwork, onCardNetworksChange} = useCardNetworkImage({assetsRef});

    const changeListener = useCallback(
        (fieldName: string) =>
            (event?: {
                dirty: boolean;
                error: string | null;
                submitted: boolean;
                touched: boolean;
                valid: boolean;
            }) => {
                if (event) {
                    const {dirty, error, submitted, touched, valid} = event;

                    if (valid) {
                        setPrimerError(prevState => ({
                            ...prevState,
                            [fieldName]: '',
                        }));
                        return;
                    }

                    if (error && touched && dirty && submitted) {
                        setPrimerError(prevState => ({
                            ...prevState,
                            checkout: '',
                            [fieldName]: error,
                        }));
                    }
                }
            },
        [setPrimerError],
    );

    const configureCard = useCallback(async () => {
        if (primerRef.current) {
            try {
                const cardManager = await primerRef.current.createPaymentMethodManager(
                    PrimerInstrumentTypes.PAYMENT_CARD,
                    {
                        onCardNetworksChange,
                    },
                );
                managerRef.current = cardManager;
                // create card's secure, Primer hosted inputs
                const {cardNumberInput, cvvInput, expiryInput} =
                    cardManager?.createHostedInputs() ?? {};

                // then render each of them into an HTML element
                await Promise.all([
                    cardNumberInput?.render(primerCardNumberOptions.id, primerCardNumberOptions),
                    cvvInput?.render(
                        primerCardSecurityCodeOptions.id,
                        primerCardSecurityCodeOptions,
                    ),
                    expiryInput?.render(primerCardExpiryOptions.id, primerCardExpiryOptions),
                ]);

                cardNumberInput?.addEventListener(
                    EventTypes.CHANGE,
                    changeListener('cardNumber-card') as unknown as EventListener,
                );
                expiryInput?.addEventListener(
                    EventTypes.CHANGE,
                    changeListener('expiryDate-card') as unknown as EventListener,
                );
                cvvInput?.addEventListener(
                    EventTypes.CHANGE,
                    changeListener('cvv-card') as unknown as EventListener,
                );
            } catch (e) {
                logWithSentry(e, 'Cart manager error occurred');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [primerRef.current, managerRef]);

    return {assetsRef, cardNetwork, configureCard};
};
